import { TTimelineChartGroups } from '../draw';
import { TDrawModel } from '../../draw.model';
import { CHART_EVENTS } from './event.tasks';
import * as d3 from 'd3';
import * as moment from 'moment';
import { TEntityActionCb, TIndicators, TRectDropElement } from '../../timeline.types';
import { DRAW_MODEL_UTILS } from '../utils';
import { flatten } from 'lodash';
import { TDate } from '@monorepo/types';

export const addStripEvent = (
  chartTemplate: TTimelineChartGroups,
  drawModel: TDrawModel,
  stripClick: TEntityActionCb,
) => {
  CHART_EVENTS.stripToolTip(chartTemplate, drawModel);
  CHART_EVENTS.stripLeftClick(chartTemplate, stripClick);
  CHART_EVENTS.stripRightClick(chartTemplate, stripClick);
};

export const addStripOnCursor = (
  elementData: TRectDropElement[],
  chartTemplate: TTimelineChartGroups,
  drawModel: TDrawModel,
  stripClick: TEntityActionCb,
) => {
  chartTemplate.body.on('mousemove.track', function (ctx, d) {
    const [x, y] = d3.pointer(ctx);
    if (x > drawModel.layout.margin.left) {
      const date = drawModel.scales.time.invert(x - drawModel.layout.margin.left + drawModel.layout.widthOfDay / 2);
      const dateStart = drawModel.scales.time(date);

      const timelinePositionGroup = d3
        .select(this)
        .selectAll('.timeline-position')
        .data([elementData])
        .join('g')
        .classed('timeline-position', true)
        .attr('x', dateStart + drawModel.layout.margin.left - drawModel.layout.widthOfDay / 2 - 5)
        .attr('y', y - 10);

      const strip = timelinePositionGroup
        .selectAll('.element')
        .data(elementData) // Use the array directly here
        .join('svg')
        .classed('element', true)
        .style('cursor', 'move')
        .attr('width', (d, i) => d.stripWidth || '120')
        .attr('height', (d, i) => 20 + i * 25) // Stagger the heights
        .attr('x', dateStart + drawModel.layout.margin.left - drawModel.layout.widthOfDay / 2 - 5)
        .attr('y', (d, i) => y - 10 - i * 25) // Stagger the vertical positions
        .on('mousedown', function (event) {
          //Since adding a strip with shift key is allowed, we prevent the default behavior of selecting text
          if (event.shiftKey) {
            event.preventDefault();
          }
        })
        .on('click', function (ctx, d) {
          const scaleGroupEntitiesY = d3.scaleLinear(
            [0, drawModel.timelineModel.comptedEntities.totalGroupEntities],
            [0, drawModel.layout.entityHeight * drawModel.timelineModel.comptedEntities.totalEntities],
          );

          const scaleY = d3.scaleLinear(
            [0, drawModel.timelineModel.comptedEntities.totalEntities],
            [0, drawModel.layout.entityHeight * drawModel.timelineModel.comptedEntities.totalEntities],
          );
          const groupIndex = Math.floor(scaleGroupEntitiesY.invert(y));
          const rowIndex = Math.floor(scaleY.invert(y));
          const entityList = flatten(drawModel.timelineModel.entities.map((v) => v.entities));
          const dateByPosition = moment(date).format('YYYY-MM-DD') as unknown as TDate;

          const stripAtLocation = entityList[rowIndex].elements.find(
            (strip) =>
              moment(strip.range.startDate).isSameOrBefore(dateByPosition) &&
              moment(strip.range.endDate).isSameOrAfter(dateByPosition),
          )?.data as unknown | undefined;

          if (ctx.shiftKey && stripAtLocation) {
            stripClick({
              type: 'STRIP_ADD',
              action: 'SHIFT+CLICK',
              entityGroupData: drawModel.timelineModel.entities[groupIndex].data,
              entityData: entityList[rowIndex].data,
              event: ctx,
              date: dateByPosition,
              stripAtLocationElementData: stripAtLocation,
              stripsTrackedOnMouse: elementData,
            });
          } else {
            stripClick({
              type: 'STRIP_TRACK',
              action: 'CLICK',
              entityGroupData: drawModel.timelineModel.entities[groupIndex].data,
              entityData: entityList[rowIndex].data,
              event: ctx,
              date: dateByPosition,
              stripsTrackedOnMouse: elementData,
            });
          }
        });

      strip
        .selectAll('.elm')
        .data((d) => [d] as TRectDropElement[])
        .join('rect')
        .classed('elm', true)
        .attr('fill', (d) => d.color)
        .attr('width', '100%')
        .attr('height', 20)
        .attr('rx', '4')
        .attr('ry', '4');

      strip
        .selectAll('.elm-text')
        .data((d) => [d])
        .join('text')
        .classed('elm-text', true)
        .text((d) => d.text.value)
        .attr('alignment-baseline', 'text-before-edge')
        .attr('x', 20)
        .attr('y', 3)
        .attr('font-size', 12)
        .attr('fill', 'white');

      strip
        .selectAll('.elm-text-border')
        .data((d) => [d])
        .join('rect')
        .classed('elm-text-border', true)
        .attr('x', 2)
        .attr('y', 2)
        .attr('width', 'calc(100% - 4px)')
        .attr('height', 'calc(100% - 4px)')
        .attr('font-weight', 'bold')
        .style('stroke', 'white')
        .style('stroke-width', 1)
        .attr('fill', 'transparent')
        .attr('rx', '4')
        .attr('ry', '4');
    }
  });
  chartTemplate.body.on('mousemove.track-date', function (ctx, d) {
    const self = this as SVGElement;
    const foregroundContainer = self.parentElement?.querySelector('.foreground-container');
    const [x, y] = d3.pointer(ctx);
    if (x > drawModel.layout.margin.left && foregroundContainer) {
      const date = drawModel.scales.time.invert(x - drawModel.layout.margin.left + drawModel.layout.widthOfDay / 2);
      const dateStart = drawModel.scales.time(date);

      const timelinePositionGroup = d3
        .select(foregroundContainer)
        .selectAll('.timeline-position')
        .data([elementData])
        .join('g')
        .classed('timeline-position', true)
        .attr('x', dateStart - drawModel.layout.widthOfDay / 2)
        .attr('y', y - 10);

      timelinePositionGroup
        .selectAll('.timeline-position-rect')
        .data([drawModel])
        .join('rect')
        .classed('timeline-position-rect', true)
        .attr('fill', 'back')
        .attr('width', '15')
        .attr('height', '15')
        .attr('x', dateStart - drawModel.layout.widthOfDay / 2 - 7.5)
        .attr('y', 0)
        .attr('rx', 10)
        .attr('ry', 10);

      timelinePositionGroup
        .selectAll('.timeline-position-text')
        .data([drawModel])
        .join('text')
        .classed('timeline-position-text', true)
        .style('fill', 'white')
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .text(moment(date).format('D'))
        .attr('x', dateStart - drawModel.layout.widthOfDay / 2)
        .attr('y', 11);
    }
  });

  chartTemplate.body.on('mousemove.track-entity', function (ctx, d) {
    const self = this as SVGElement;
    const leftNav = self.querySelector('.left-nav-background');
    const [x, y] = d3.pointer(ctx);
    const entityIndex = Math.floor(drawModel.scales.entity(y));

    const leftNavD3 = d3.select(leftNav);

    leftNavD3
      .selectAll('.entity-active')
      .data([drawModel])
      .join('rect')
      .classed('entity-active', true)
      .attr('x', 0)
      .attr('y', entityIndex * drawModel.layout.entityHeight)
      .attr('width', drawModel.layout.margin.left)
      .attr('height', drawModel.layout.entityHeight)
      .attr('fill', '#3879db')
      .attr('fill-opacity', 0.4);
  });
};

export const removeCursor = (chartTemplate: TTimelineChartGroups) => {
  console.log('Remove cursor', chartTemplate.body.select('.timeline-position'));
  chartTemplate.body.select('.timeline-position').remove();
  chartTemplate.body.select('.entity-active').remove();
  chartTemplate.foreground.select('.timeline-position').remove();
  chartTemplate.body.on('mousemove.track', null);
  chartTemplate.body.on('mousemove.track-date', null);
  chartTemplate.body.on('mousemove.track-entity', null);
};

//vertical line with arrow head just like the one on today
export const addMarkers = (chartTemplate: TTimelineChartGroups, drawModel: TDrawModel, markers: TIndicators[]) => {
  const getArrowHeads = (color: string) => {
    const id = `arrow-${color.replace('#', '')}`;

    if (!chartTemplate.body.selectAll('defs').select(`#${id}`).empty()) {
      return id;
    }

    chartTemplate.body
      .selectAll('defs')
      .append('marker')
      .attr('id', id)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', color);

    return id;
  };

  const foreground = chartTemplate.foreground;

  // Clear existing markers
  foreground.selectAll('.marker').remove();

  foreground
    .selectAll('.marker')
    .data(markers)
    .join('line')
    .classed('marker', true)
    .style('stroke', (d) => d.color)
    .style('stroke-dasharray', '3, 3')
    .style('stroke-width', 3)
    .attr('x1', (d) => {
      const position = DRAW_MODEL_UTILS.getPostionByDate(d.date, drawModel);
      // Adjust position based on indicator position preference
      return (
        position + (d.position === 'END_OF_DAY' ? drawModel.layout.widthOfDay / 2 : -drawModel.layout.widthOfDay / 2)
      );
    })
    .attr('y1', drawModel.timelineModel.comptedEntities.totalEntities * drawModel.layout.entityHeight + 'px')
    .attr('x2', (d) => {
      const position = DRAW_MODEL_UTILS.getPostionByDate(d.date, drawModel);
      // Same adjustment for x2
      return (
        position + (d.position === 'END_OF_DAY' ? drawModel.layout.widthOfDay / 2 : -drawModel.layout.widthOfDay / 2)
      );
    })
    .attr('y2', 2)
    .attr('marker-end', (d) => `url(#${getArrowHeads(d.color)})`);
};
